import React from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { Colors } from '../constants/Colors';
import { InputError } from './InputError';
import { TextReg } from './StyledText';
import { TextInput } from './TextInput';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  maxLength: number;
  multiline?: boolean;
  numberOfLines?: number;
  textAlignVertical?: 'auto' | 'top' | 'bottom' | 'center';
  fieldError?: { message?: string };
  style?: StyleProp<TextStyle>;
  placeholder?: string;
  dense?: boolean;
  counterPosition?: 'top' | 'bottom';
};

export const TextInputWithCounter = ({
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  maxLength,
  multiline = false,
  numberOfLines,
  textAlignVertical,
  fieldError,
  style,
  placeholder,
  dense,
  counterPosition = 'bottom',
}: Props) => {
  const textLength = (value || '').length;
  const remainingPercent = (maxLength - textLength) / maxLength;
  const isNearLimit = remainingPercent < 0.1;
  const isOverLimit = remainingPercent < 0;

  const counterElement = (
    <TextReg
      style={[
        styles.counter,
        isOverLimit
          ? styles.counterOverLimit
          : isNearLimit
            ? styles.counterNearLimit
            : styles.counterNormal,
      ]}
    >
      {textLength}/{maxLength}
    </TextReg>
  );

  const headerRow =
    counterPosition === 'top' &&
    (label ? (
      <View style={styles.headerRow}>
        <TextReg style={styles.label}>{label}</TextReg>
        {counterElement}
      </View>
    ) : (
      <View style={styles.counterOnlyRow}>{counterElement}</View>
    ));

  const bottomRow = counterPosition === 'bottom' && (
    <View style={styles.bottomRow}>
      {fieldError?.message ? (
        <TextReg style={styles.errorText}>{fieldError.message}</TextReg>
      ) : (
        <View />
      )}
      {counterElement}
    </View>
  );

  return (
    <View style={styles.container}>
      {headerRow}
      <TextInput
        label={counterPosition === 'bottom' ? label : ''}
        showLabel={counterPosition === 'bottom' && !!label}
        multiline={multiline}
        numberOfLines={numberOfLines}
        textAlignVertical={textAlignVertical}
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        maxLength={maxLength}
        style={style}
        error={Boolean(fieldError)}
        placeholder={placeholder}
        dense={dense}
      />
      {bottomRow}
      {counterPosition === 'top' && fieldError && (
        <InputError fieldError={fieldError} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  counterOnlyRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 4,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  errorText: {
    color: Colors.orange0,
    fontSize: 12,
  },
  label: {
    fontSize: 14,
  },
  counter: {
    fontSize: 12,
  },
  counterNormal: {
    color: Colors.opacity50,
  },
  counterNearLimit: {
    color: Colors.yellow0,
  },
  counterOverLimit: {
    color: Colors.pink0,
  },
});
